import { DownOutlined } from "@ant-design/icons";
import { MenuProps } from "antd";
import { useExportForumAttendeeMeetingLocationsQuery } from "api/queries/attendeeMeetingLocations";
import { Dropdown } from "components/styleguide";
import { AttendeeStatus, TimeSlotType } from "generated/api";
import { toNumber } from "lodash";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { downloadFileByURL } from "utils/fileUtils";

interface MeetingLocationsExportButtonProps {
  timeslotId?: number;
  timeType?: TimeSlotType;
  exportByTimeType?: boolean;
  groupIds?: number[];
  peopleTypes?: string[];
  facilities?: number[];
  attendeeName?: string;
  companyName?: string;
  attendeeStatuses?: AttendeeStatus[];
}

enum MeetingLocationsExportType {
  Export,
  ExportAll,
}

const MeetingLocationsExportButton: FC<MeetingLocationsExportButtonProps> = ({
  timeslotId,
  timeType,
  exportByTimeType = false,
  groupIds,
  peopleTypes,
  facilities,
  attendeeName,
  companyName,
  attendeeStatuses,
}) => {
  const forumId = toNumber(useParams().id);
  const { t } = useTranslation();

  const [exportType, setExportType] = useState<MeetingLocationsExportType | null>(null);

  const { isFetching: isExportMeetingLocationsLoading } = useExportForumAttendeeMeetingLocationsQuery(
    {
      forumId,
      forumGroupIds: groupIds,
      timeSlotId: !exportByTimeType && exportType === MeetingLocationsExportType.Export ? timeslotId : undefined,
      timeType: exportByTimeType && exportType === MeetingLocationsExportType.Export ? timeType : undefined,
      exportByTimeType,
      forumAttendeeParticipationCodes: peopleTypes,
      forumFacilityIds: facilities,
      forumAttendeeName: attendeeName,
      forumCompanyName: companyName,
      attendeeStatuses,
    },
    {
      enabled: exportType !== null,
      refetchOnWindowFocus: false,
      onSuccess: ({ data }) => downloadFileByURL(data),
      onError: () => toast.error(t("Something went wrong")),
      onSettled: () => setExportType(null),
    },
  );

  const onExport = () => {
    if (exportByTimeType && !timeType) {
      toast.error(t("The time type must be selected"));
      return;
    }

    if (!exportByTimeType && !timeslotId) {
      toast.error(t("The timeslot must be selected"));
      return;
    }

    setExportType(MeetingLocationsExportType.Export);
  };

  const onMenuItemClick: MenuProps["onClick"] = (e) => {
    if (toNumber(e.key) === MeetingLocationsExportType.ExportAll) {
      setExportType(MeetingLocationsExportType.ExportAll);
    }
  };

  const items = [
    {
      label: t(exportByTimeType ? "Export All Time Types" : "Export All Timeslots"),
      key: MeetingLocationsExportType.ExportAll,
    },
  ];

  const menu = {
    items,
    onClick: onMenuItemClick,
  };

  return (
    <Dropdown.Button menu={menu} icon={<DownOutlined />} loading={isExportMeetingLocationsLoading} onClick={onExport}>
      {t("Export")}
    </Dropdown.Button>
  );
};

export default MeetingLocationsExportButton;
